<script lang="ts" setup>
import type { ContentCardVariants } from '~ds/blocks/content-card';

import { NuxtLink } from '#components';
import { ContentCard } from '~ds/blocks/content-card';
import { Badge } from '~ds/components/ui/badge';
import { isAnywayPartner } from '~kui/components/KUI/Unified/Asset/assetCardSponsorship';
import { useI18n } from 'vue-i18n';

import type { ArticleCard } from './types';

import { contentCardPicture } from './contentCardPicture';
import KUIUnifiedContentPartnerLogo from './KUIUnifiedContentPartnerLogo.vue';

/**
 * One article in a row, the same on a hub and an index page: the cover badged with the
 * article's template, then the title and reading time. The whole card is the link. `size` grows
 * it into a featured block's cell-filling or lead card.
 */
interface Props {
  article: ArticleCard;
  size?: ContentCardVariants['size'];
}

const props = withDefaults(defineProps<Props>(), {
  size: 'row',
});

const picture = computed(() => contentCardPicture(props.size));

const { t } = useI18n();

// A partner's article carries its logo, as an experience card does; Anyway's own articles do not.
const partnerLogoUrl = computed(() => (props.article.partnerLogoUrl && !isAnywayPartner(props.article.partnerName) ? props.article.partnerLogoUrl : undefined));

const partnerName = computed(() => (props.article.partnerName && !isAnywayPartner(props.article.partnerName) ? props.article.partnerName : undefined));

// A featured card names the partner that wrote it; Anyway's own articles name nobody.
const byline = computed(() => (props.size !== 'row' && partnerName.value
  ? t('unified.learnIa.byPartner', { partner: partnerName.value })
  : undefined));
</script>

<template>
  <ContentCard
    :size="size"
    :as="NuxtLink"
    :to="article.to"
    :title="article.name"
    :description="byline"
    :aria-label="partnerName
      ? $t('unified.hubs.articles.readByPartner', { title: article.name, partner: partnerName })
      : $t('unified.hubs.articles.read', { title: article.name })"
    :meta="article.readingTimeMinutes ? $t('unified.articles.minRead', { count: article.readingTimeMinutes }) : undefined"
    data-testid="article-card"
  >
    <template #image>
      <NuxtPicture
        :src="article.imageUrl"
        format="avif,webp"
        alt=""
        v-bind="picture"
      />
    </template>
    <template #badge>
      <Badge
        variant="solid"
        tone="lime"
        dot
        data-testid="article-card-badge"
      >
        {{ article.badge }}
      </Badge>
    </template>
    <template v-if="partnerLogoUrl" #corner>
      <KUIUnifiedContentPartnerLogo
        :src="partnerLogoUrl"
        :partner-name="article.partnerName ?? ''"
        data-testid="article-card-partner"
      />
    </template>
  </ContentCard>
</template>
